import { Component, computed, DestroyRef, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { InvitationsService, InvitationType } from 'core/services/invitations.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ReferralModalFacade } from '../../../../shared/services/referral-modal.facade';
import { Observable, tap } from 'rxjs';
import { IInvitation } from 'core/types/invitation.interface';
import { KorviuButtonComponent } from 'shared/components/korviu-button/korviu-button.component';
import { KorviuListComponent } from 'shared/components/korviu-list/korviu-list.component';
import { KorviuNoDataComponent } from 'shared/components/korviu-no-data/korviu-no-data.component';
import { GoBackComponent } from 'shared/components/go-back/go-back.component';
import { TabsV2Component } from 'shared/components/tabs-v2/tabs-v2.component';
import { AuthService } from 'core';
import { EmptyTemplateDirective, HideTooltipOnScrollDirective } from 'shared';
import { User } from 'shared/types/user.model';
import { KorviuListItemTypes } from 'shared/types/korviu-list.config';
import { IPagination } from 'shared/types/pagination.interface';
import { IFilter } from 'shared/types/filter.interface';
import { ISorting } from 'shared/types/sorting.interface';
import { IGetPaginatedResponse } from 'shared/types/get-paginated-response.interface';
import { SortOrders } from 'shared/types/sort-order.enum';
import { NavigationService } from 'core/services/navigation.service';
import { ReferralUpdatesService } from '../../../../shared/services/referral-updates.service';

enum ETabs {
  MEMBERS,
  GANASOCIOS
}

type ReferralEmptyState = 'empty' | 'no-matches' | null;

@Component({
  selector: 'app-my-referrals-my-info-special',
  imports: [
    KorviuButtonComponent,
    KorviuListComponent,
    NzTooltipDirective,
    TranslatePipe,
    GoBackComponent,
    TabsV2Component,
    HideTooltipOnScrollDirective,
    EmptyTemplateDirective,
    KorviuNoDataComponent,
    NgTemplateOutlet
  ],
  templateUrl: './my-referrals-my-info-special.component.html',
  styleUrl: './my-referrals-my-info-special.component.scss'
})
export class MyReferralsMyInfoSpecialComponent {
  readonly referralsList = viewChild(KorviuListComponent);

  #invitationService = inject(InvitationsService);
  #router = inject(Router);
  #activatedRoute = inject(ActivatedRoute);
  #referralModalFacade = inject(ReferralModalFacade);
  #authService = inject(AuthService);
  #navigationService = inject(NavigationService);
  #referralUpdates = inject(ReferralUpdatesService);
  #destroyRef = inject(DestroyRef);

  readonly currentUser = computed<User | null>(() => this.#authService.currentUser$());

  readonly selectedTab = signal<number>(ETabs.MEMBERS);
  readonly membersEmptyState = signal<ReferralEmptyState>(null);
  readonly ganasociosEmptyState = signal<ReferralEmptyState>(null);
  readonly emptyState = computed(() =>
    this.selectedTab() === ETabs.MEMBERS ? this.membersEmptyState() : this.ganasociosEmptyState()
  );

  ETabs = ETabs;

  tabs = signal([
    { id: ETabs.MEMBERS, label: 'myGanarbols.members' },
    { id: ETabs.GANASOCIOS, label: 'my-forest.ganasocios' }
  ]);

  membersListConfig = {
    getFn: this.#getMembers.bind(this),
    detailsFn: this.#openSeedInfo.bind(this),
    hideSearch: false,
    doNotSortFilterSections: true,
    filterSections: [
      {
        name: '',
        localizableName: 'common.reminderFilter.title',
        parameter_name: 'reminder',
        checkboxModel: [
          {
            label: '',
            localizableName: 'common.reminderFilter.invitation',
            value: 'invitation',
            parameter_name: 'invitation'
          },
          { label: '', localizableName: 'common.reminderFilter.first', value: 'first', parameter_name: 'first' },
          { label: '', localizableName: 'common.reminderFilter.second', value: 'second', parameter_name: 'second' },
          { label: '', localizableName: 'common.reminderFilter.third', value: 'third', parameter_name: 'third' }
        ]
      }
    ],
    sortSections: [
      {
        localizableName: 'common.sortOption.invitationDate',
        options: [
          {
            localizableName: 'common.sortOption.newest',
            sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldest',
            sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'common.sortOption.lastContacted',
        options: [
          {
            localizableName: 'common.sortOption.newest',
            sortDirection: { sortBy: 'last_contacted_date', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldest',
            sortDirection: { sortBy: 'last_contacted_date', sortOrder: SortOrders.ASCEND }
          }
        ]
      }
    ],
    itemType: KorviuListItemTypes.REFERRAL,
    enableBulkRemoval: true,
    totalLocalizableName: 'buildYourNetwork.seed.header'
  };

  ganasociosListConfig = {
    getFn: this.#getGanasocios.bind(this),
    detailsFn: this.#openSeedInfo.bind(this),
    hideSearch: false,
    hideFilterSection: true,
    filterSections: [],
    sortSections: [
      {
        localizableName: 'common.sortOption.invitationDate',
        options: [
          {
            localizableName: 'common.sortOption.newest',
            sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldest',
            sortDirection: { sortBy: 'timestamp', sortOrder: SortOrders.ASCEND }
          }
        ]
      },
      {
        localizableName: 'common.sortOption.lastContacted',
        options: [
          {
            localizableName: 'common.sortOption.newest',
            sortDirection: { sortBy: 'last_contacted_date', sortOrder: SortOrders.DESCEND }
          },
          {
            localizableName: 'common.sortOption.oldest',
            sortDirection: { sortBy: 'last_contacted_date', sortOrder: SortOrders.ASCEND }
          }
        ]
      }
    ],
    itemType: KorviuListItemTypes.REFERRAL_GANASOCIOS,
    enableBulkRemoval: true,
    totalLocalizableName: 'buildYourNetwork.seed.header'
  };

  constructor() {
    this.#referralUpdates.changes$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.referralsList()?.reloadData(true, false));
  }

  addReferral(): void {
    this.#referralModalFacade.openAddReferralModal();
  }

  exploreMyForest(): void {
    this.#navigationService.setNextGoBackUrl('/workspace/my-referrals');
    void this.#router.navigate(['/workspace/my-forest']);
  }

  inviteReferral(): void {
    this.#referralModalFacade.openAddReferralModal();
  }

  clearSearchAndFilters(): void {
    this.referralsList()?.clearSearchAndFilters();
  }

  #getMembers(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IGetPaginatedResponse<IInvitation[]>> {
    return this.#getReferrals(InvitationType.GANACUATES, pagination, filters, sorting, this.membersEmptyState);
  }

  #getGanasocios(
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting
  ): Observable<IGetPaginatedResponse<IInvitation[]>> {
    return this.#getReferrals(InvitationType.GANASOCIOS, pagination, filters, sorting, this.ganasociosEmptyState);
  }

  #getReferrals(
    type: InvitationType,
    pagination: IPagination,
    filters: IFilter[],
    sorting: ISorting,
    emptyState: WritableSignal<ReferralEmptyState>
  ): Observable<IGetPaginatedResponse<IInvitation[]>> {
    emptyState.set(null);

    return this.#invitationService.getReferralList(type, pagination, filters, sorting).pipe(
      tap(response => {
        if (response.count === 0) {
          emptyState.set(filters.length ? 'no-matches' : 'empty');
        }
      })
    );
  }

  #openSeedInfo(seedId: number): void {
    this.#router.navigate(['referral', seedId], { relativeTo: this.#activatedRoute });
  }
}
